/**
 * Timeline Height Composable
 * Calculates dynamic height based on visible rows
 */

import { computed, type Ref } from 'vue';
import { UserData, PaginatedData } from '../utils/types';
import { sortTimelineData } from '../utils/timelineDataTransformers';

/**
 * Derives visible slice matching timeline display logic
 */
function deriveVisibleSlice(
    fullData: UserData[],
    groupBy: string,
    currentPage: number,
    pageSize: number
): UserData[] {
    if (!fullData || fullData.length === 0) return [];

    // If not grouping by teams, simple user slice
    if (groupBy !== 'TEAMS') {
        const start = (currentPage - 1) * pageSize;
        return fullData.slice(start, start + pageSize);
    }

    // Build team map keyed by teamId
    interface TeamBucket { teamId: number; teamName: string; users: UserData[] }
    const teamMap = new Map<number, TeamBucket>();
    fullData.forEach((userData) => {
        const teamId = userData.user?.teamId ?? -1;
        const teamName = userData.user?.teamName || 'Blanks';
        if (!teamMap.has(teamId)) {
            teamMap.set(teamId, {
                teamId, teamName, users: []
            });
        }
        teamMap.get(teamId)!.users.push(userData);
    });

    // Sort teams by teamName
    const teamsArray = Array.from(teamMap.values()).sort((a, b) => a.teamName.localeCompare(b.teamName));
    const start = (currentPage - 1) * pageSize;
    const visibleTeams = teamsArray.slice(start, start + pageSize);

    // Flatten users of visible teams
    return visibleTeams.flatMap((t) => t.users);
}

/**
 * Composable for calculating timeline height dynamically
 * @param options - Configuration object containing all required parameters
 * @returns Computed height string
 */
export const useTimelineHeight = (options: {
    filteredData: Ref<PaginatedData | null>;
    groupBy: Ref<string>;
    currentPage: Ref<number>;
    pageSize: Ref<number>;
    collapsedGroups?: Ref<Set<string | number>>;
}) => {
    const { filteredData, groupBy, currentPage, pageSize, collapsedGroups } = options;
    const ROW_HEIGHT = 62; // Each row is 62px as defined in CSS (18px shift + 28px task + spacing)
    const HEADER_HEIGHT = 100; // Major label (60px) + Minor label (40px) header height
    const BOTTOM_PADDING = 0; // Extra padding at the bottom
    const MIN_HEIGHT = 0; // Minimum height

    const timelineHeight = computed(() => {
        const data = filteredData.value;
        if (!data || !data.data) return `${MIN_HEIGHT}px`;
        
        // Sort and slice data using the same logic as timeline
        const sortedData = sortTimelineData(data.data, groupBy.value);
        const visibleData = deriveVisibleSlice(
            sortedData,
            groupBy.value,
            currentPage.value,
            pageSize.value
        );
        
        let rowCount = 0;
        
        if (groupBy.value === 'TEAMS') {
            // When grouped by teams, count team headers + user rows (unless collapsed)
            const teamMap = new Map<number, number>();
            visibleData.forEach((userData: UserData) => {
                const teamId = userData.user.teamId || 0;
                if (!teamMap.has(teamId)) {
                    teamMap.set(teamId, 0);
                    rowCount++; // Count team header
                }
                // Only count user rows if group is not collapsed
                const isCollapsed = collapsedGroups?.value?.has(`team-${teamId}`);
                if (!isCollapsed) {
                    teamMap.set(teamId, teamMap.get(teamId)! + 1);
                    rowCount++; // Count user row
                }
            });
        } else {
            // When not grouped, just count user rows
            rowCount = visibleData.length;
        }
        
        const calculatedHeight = HEADER_HEIGHT + (rowCount * ROW_HEIGHT) + BOTTOM_PADDING;
        return `${Math.max(calculatedHeight, MIN_HEIGHT)}px`;
    });

    return { timelineHeight };
};
